// CAUTION
//
// Google Analytics Paid Media Tracking
// These routes are utilized by GA.
//
import { ActivityTypeGroups, MedicalNoteType } from '@uplift/api';
import { AvailabilitySettingsModalType, CalendarEditView } from '@uplift/types';

// -- !!! Please inform the team of your changes :) !!! --
import { createTypedDynamicRoute } from './create-typed-dynamic-route';

export const patientIntakeFormRoute = createTypedDynamicRoute<{
  patientId: string;
  submissionId: string;
}>(params => `/clients/${params.patientId}/intake-form/${params.submissionId}`);

export const providerScheduleViewTypeRoute = createTypedDynamicRoute<
  Record<string, never>,
  {
    editView?: CalendarEditView;
    createAppointment?: 'add' | 'invite';
    modal?: AvailabilitySettingsModalType;
  }
>(_params => '/schedule');

export const providerSubmitSessionRoute = createTypedDynamicRoute<
  { appointmentId: string },
  { redirect_to?: string }
>((params) => `/appointments/${params.appointmentId}/submit-session`);

export const findPsychiatryRoute = createTypedDynamicRoute<{}, { source?: string }>(() => '/find-psychiatrist');

export const providerPatientRoute = createTypedDynamicRoute<
  {
    patientId: string;
  },
  {
    activityType?: ActivityTypeGroups;
    activityId?: string;
  }
>(params => `/clients/${params.patientId}`);

export const collaborationProviderProfileRoute = createTypedDynamicRoute<{
  superviseeId: string;
}>(params => `/collaboration/manage/${params.superviseeId}`);

export const providerSuperviseeChartsReviewForMatchRoute = createTypedDynamicRoute<{
  collaborativeMatchId: string;
}>(params => `/collaboration/manage/charts-review-for-match/${params.collaborativeMatchId}`);


export const providerChartsReviewRoute = createTypedDynamicRoute<{
  reviewId: string;
}>(params => `/collaboration/manage/charts-review/${params.reviewId}`);

export const careTeamProviderRoute = createTypedDynamicRoute<{ id: string }>(
  params => `/care-team/${params.id}`
);

export const appointmentSessionRoute = createTypedDynamicRoute<{
  appointmentId: string;
}>(params => `/appointments/${params.appointmentId}/session`);

export const notesRoute = createTypedDynamicRoute<{
  patientId: string;
  noteType: MedicalNoteType;
  noteId?: string;
}>(params => `/clients/${params.patientId}/note/${params.noteType}/${params.noteId ?? ''}`);

export const claimCorrections = createTypedDynamicRoute<{
  patientId: string;
}>(params => `/clients/${params.patientId}/claim-corrections`);

export const adminUserProfile = createTypedDynamicRoute<{
  userId: string;
  // tab: 'details' | 'credentialing' | 'availability' | 'activity';
}>(params => `/profile/${params.userId}`);

export const adminUserProfileDetails = createTypedDynamicRoute<{ userId: string }>(params => `/profile/${params.userId}/details`);
export const adminUserProfileOnboarding = createTypedDynamicRoute<{ userId: string }>(params => `/profile/${params.userId}/onboarding`);
export const adminUserProfileCredentialing = createTypedDynamicRoute<{ userId: string }>(params => `/profile/${params.userId}/credentialing`);
export const adminUserProfileAvailability = createTypedDynamicRoute<{ userId: string }>(params => `/profile/${params.userId}/availability`);
export const adminUserProfileActivity = createTypedDynamicRoute<{ userId: string }>(params => `/profile/${params.userId}/activity`);
export const adminProfileNotes = createTypedDynamicRoute<{ userId: string }>(params => `/profile/${params.userId}/notes`);
export const adminProfileSupervisees = createTypedDynamicRoute<{ userId: string }>(params => `/profile/${params.userId}/supervisees`);
export const adminProfileSessionHistory = createTypedDynamicRoute<{ userId: string }>(params => `/profile/${params.userId}/session-history`);

export const adminAgreementTemplateEdit = createTypedDynamicRoute<{ id: string }>(params => `/tools/agreement-templates/${params.id}/edit`);

export const adminPatientAppointments = createTypedDynamicRoute<{
  userId: string;
}>(params => `/profile/${params.userId}/patient-appointments`);

export const onboardingError = createTypedDynamicRoute<
  Record<string, never>,
  { error?: 'invalid_invite' }
>(() => '/onboarding/error');

export const commonError = createTypedDynamicRoute<
  Record<string, never>,
  { error?: string }
>(() => '/error');

export const paymentsTabRoute = createTypedDynamicRoute<
  { tab: 'overview' | 'method' | 'coverage' },
  { payBalance?: string }
>(params => `/payments/${params.tab}`);

export const higherEducationProfile = createTypedDynamicRoute<{
  id: string;
}>(params => `/higher-education-profile/${params.id}`);

export const higherEducationProfileDetails = createTypedDynamicRoute<{ id: string }>(params => `/higher-education-profile/${params.id}/details`);

export const higherEducationManagerProfile = createTypedDynamicRoute<{
  managerId: string;
}>(params => `/higher-education/manager-profile/${params.managerId}`);

export const higherEducationManagerProfileDetails = createTypedDynamicRoute<{ managerId: string }>(params => `/higher-education/manager-profile/${params.managerId}/details`);
export const settingsRoute = createTypedDynamicRoute<{}, { edit: 'emergencyContact' | 'phone' | 'address' | 'pronouns' | 'name' | 'dob' | 'sex' }>(() => '/settings');

export const clientScheduleSessionRoute = createTypedDynamicRoute<{
  providerId: string;
}>(params => `/schedule-session/${params.providerId}`);

export const practiceStateSetupRoute = createTypedDynamicRoute<{ stateId: string }>(
  params => `/practice-states/${params.stateId}/setup`
);

export const stateDetailsRoute = createTypedDynamicRoute<{ stateId: string }>(
  params => `/states/${params.stateId}`
);

export const supportArticleRoute = createTypedDynamicRoute<{
  slug: string;
}>(params => `/support/articles/${params.slug}`);

export const supportCategoryRoute = createTypedDynamicRoute<{
  category: string;
}>(params => `/support/categories/${params.category}`);


export const formBySubmissionRoute = createTypedDynamicRoute<{
  submissionId: string;
}>(params => `/form/submission/${params.submissionId}`);

export const ROUTING_CONFIG = {
  home: '/',
  reports: '/reports/upcoming',

  providerMatchingFieldsReport: '/reports/provider_matching_fields',
  providerLicenses: '/reports/provider_licenses',
  providerLicensesExpiring: '/reports/provider_licenses_expiring',
  providerLicensesExpired: '/reports/provider_licenses_expired',
  credentialingLicenseStatus: '/reports/credentialing/license_status',
  credentialingCredentialingStatus: '/reports/credentialing/credentialing_statuses',
  clientMultipleSeries: '/reports/client_multiple_series',
  providerUnsubmittedSessions: '/reports/provider_unsubmitted_sessions',
  providerCancelledSessions: '/reports/provider_cancelled_sessions',
  clientOutstandingBalance: '/reports/client_outstanding_balance',
  clientClaimsSubmission: '/reports/claims_submission',
  reportsPast: '/reports/past',

  matching: '/matching',

  // Admin Pages
  adminTaskExecution: '/taskExecution',
  admins: '/admins',
  tags: '/tags',
  collaborativePhysicians: '/collaborative-physicians',
  adminUserProfile: adminUserProfile.template(),
  adminUserProfileDetails: adminUserProfileDetails.template(),
  adminUserProfileOnboarding: adminUserProfileOnboarding.template(),
  adminUserProfileCredentialing: adminUserProfileCredentialing.template(),
  adminUserProfileAvailability: adminUserProfileAvailability.template(),
  adminUserProfileActivity: adminUserProfileActivity.template(),
  adminPatientAppointments: adminPatientAppointments.template(),
  adminProfileSupervisees: adminProfileSupervisees.template(),
  adminProfileNotes: adminProfileNotes.template(),
  adminProfileSessionHistory: adminProfileSessionHistory.template(),
  adminMatchingView: '/matching-view',

  upload: '/upload/',
  utils: '/utils',
  providersActivation: '/providers-activation',
  rostering: '/rostering',
  messaging: '/messaging',
  messagingReport: '/messaging-report',
  insuranceMarkets: '/insurance-markets',
  verifiableLicenseMapping: '/verifiable-license-mapping',
  states: '/states',
  stateDetails: stateDetailsRoute.template(),
  businessEntities: '/businessEntities',
  chartDownload: '/chart-download',
  toolsFinancePayouts: '/tools/finance-payouts',
  toolsAgreementTemplates: '/tools/agreement-templates',
  adminAgreementTemplateEdit: adminAgreementTemplateEdit.template(),
  toolsVendorSync: '/tools/vendor-sync',
  practiceStates: '/practice-states',
  practiceStateSetup: practiceStateSetupRoute.template(),

  // Higher Education Admin
  adminHigherEducation: '/higher-education',
  higherEducationNew: '/higher-education/new',
  higherEducationProfile: higherEducationProfile.template(),
  higherEducationProfileDetails: higherEducationProfileDetails.template(),
  higherEducationManagerProfile: higherEducationManagerProfile.template(),
  higherEducationManagerProfileDetails: higherEducationManagerProfileDetails.template(),

  // Care App
  organizationSettings: '/organization-settings',

  createAccount: '/create-account',
  aboutYou: '/about-you',
  inviteInvalid: '/invite-invalid',
  commonError: commonError.template(),

  //
  login: '/auth/login',
  registration: '/auth/registration',
  resetPassword: '/auth/reset-password',
  setPassword: '/auth/set-password',
  betterhelpSsoLanding: '/sso/betterhelp',
  main: '/main',
  payments: '/payments',
  documentsCenter: '/documents-center',
  insurance: '/insurance',
  paymentsTab: paymentsTabRoute.template(),
  schedule: providerScheduleViewTypeRoute.template(),
  finance: '/finance',
  financeMonth: '/finance/:date/:filter',
  support: '/support',
  supportArticle: supportArticleRoute.template(),
  supportCategory: supportCategoryRoute.template(),
  any: '*',
  appointmentsSession: appointmentSessionRoute.template(),
  appointmentsSubmitSession: providerSubmitSessionRoute.template(),
  patients: '/clients',
  collaborationManage: '/collaboration/manage',
  collaborationManageProfile: collaborationProviderProfileRoute.template(),
  collaborationManageChartsReviewForMatch: providerSuperviseeChartsReviewForMatchRoute.template(),
  chartsReviews: '/charts-review',
  chartsReview: providerChartsReviewRoute.template(),
  patient: providerPatientRoute.template(),
  patientIntakeForm: patientIntakeFormRoute.template(),
  notSubmittedSessions: '/notsubmitted-sessions',
  pageNotFound: '/404',
  profile: '/profile',
  careTeam: '/care-team',
  careTeamProfile: careTeamProviderRoute.template(),
  providerProfile: '/profile/:id',
  rematching: '/rematching/:id',
  deeplinkRematching: '/rematching',
  settings: settingsRoute.template(),
  billing: '/settings/billing',
  dashboard: '/dashboard',
  termsOfService: '/terms-of-service',
  privacyPolicy: '/privacy-policy',
  notes: notesRoute.template(),
  claimCorrections: claimCorrections.template(),

  // provider onboarding
  invitation: '/invitation',
  invitationInsurance: '/invitation/insurance',
  invitationPayment: '/invitation/payment',
  onboardingWelcome: '/onboarding/welcome',
  onboardingLoginInfo: '/onboarding/login-info',
  onboardingInvite: '/onboarding/invite',
  onboardingGeneral: '/onboarding/general',
  onboardingPractice: '/onboarding/practice',
  onboardingClinical: '/onboarding/clinical',
  onboardingAvailability: '/onboarding/availability',
  onboardingFinance: '/onboarding/finance',
  onboardingComplete: '/onboarding/complete',
  onboardingPayorEnrollment: '/onboarding/payor-enrollment',
  onboardingError: onboardingError.template(),

  legacyOnboard: '/onboard',
  legacyOnboardConnect: '/onboard/connect',

  // form builder
  allForms: '/forms',
  importForm: '/form/import',
  formTags: '/form/tags',
  newFormTag: '/form/tags/:formTagId',
  form: '/form/:formId',
  previewForm: '/form/:formId/preview/:page',
  formElement: '/form/:formId/element/:elementId',
  newFormElement: '/form/:formId/element/:elementId/:type',
  formGroup: '/form/:formId/group/:formGroupId',
  submissionsList: '/form/:formId/submissions',
  mappings: '/formMappings',

  // form submit
  formBySubmission: formBySubmissionRoute.template(),
  formStart: '/form/:submissionId/start', // reserving for use
  formFinish: '/form/:submissionId/finish', // reserving for use
  formPage: '/form/:submissionId/page/:page',

  featureFlags: '/featureFlags',

  nextAssessment: '/nextAssessment',

  findPsychiatrist: findPsychiatryRoute.template(),

  publicProviderProfile: '/provider/:medicalRole/:providerData',

  profileUrlList: '/profile-urls',
  clientScheduleSession: clientScheduleSessionRoute.template(),
};

export const PATIENT_ONBOARDING_ROUTING_CONFIG = {
  onboarding: '/onboarding',
  welcome: '/onboarding/welcome',
  where: '/onboarding/where',
  where1: '/onboarding/where1',
  notAvailable: '/onboarding/not-available',
  insurance: '/onboarding/insurance',
  who: '/onboarding/who',
  reason: '/onboarding/reason',
  age: '/onboarding/age',
  tooYoung: '/onboarding/too-young',
  providerFilters: '/onboarding/provider-filters',
  providerIdentityFilter: '/onboarding/provider-identity-filter',
  providerExperienceFilter: '/onboarding/provider-experience-filter',
  levelOfCare: '/onboarding/level-of-care',
  assessmentEntry: '/onboarding/assessment',
  assessmentQuestion1: '/onboarding/assessment/phq2/1',
  assessmentQuestion2: '/onboarding/assessment/phq2/2',
  assessmentQuestion3: '/onboarding/assessment/gad2/1',
  assessmentQuestion4: '/onboarding/assessment/gad2/2',
  assessmentMedHistory: '/onboarding/assessment/med-history',
  assessmentHasTherapist: '/onboarding/assessment/has-therapist',
  assessmentIsTherapistGoodFit: '/onboarding/assessment/is-therapist-good-fit',
  assessmentResult: '/onboarding/assessment/result',
  createAccount: '/onboarding/create-account',
  matchesLoading: '/onboarding/matches-loading',
  matches: '/onboarding/matches',
  match: '/onboarding/match',
  aboutYou: '/onboarding/about-you',
  insuranceInfo: '/onboarding/insurance-info',
  paymentInfo: '/onboarding/payment-info',
  confirmBooking: '/onboarding/confirm-booking',
  bookingSuccess: '/onboarding/booking-success',
  psychInterstitial: '/onboarding/booking-both-psych-interstitial',
  bookingBothPsychMatches: '/onboarding/booking-both-psych-matches',
  bookingBothPsychMatch: '/onboarding/booking-both-psych-match',
  bookingBothPsychInsurance: '/onboarding/booking-both-psych-insurance',
} as const;
export const PATIENT_ONBOARDING_ROUTES: string[] = Object.values(PATIENT_ONBOARDING_ROUTING_CONFIG);

export const STUDENT_ONBOARDING_ROUTING_CONFIG = {
  onboarding: '/student/onboarding',
  invalidLink: '/student/onboarding/invalid-link',
  welcome: '/student/onboarding/welcome',
  createAccount: '/student/onboarding/create-account',
  verifyPhone: '/student/onboarding/verify-phone',
  verifyPhoneCode: '/student/onboarding/verify-phone-code',
  where: '/student/onboarding/where',
  notAvailable: '/student/onboarding/not-available',
  insurance: '/student/onboarding/insurance',
  age: '/student/onboarding/age',
  tooYoung: '/student/onboarding/too-young',
  reason: '/student/onboarding/reason',
  providerFilters: '/student/onboarding/provider-filters',
  providerIdentityFilter: '/student/onboarding/provider-identity-filter',
  providerExperienceFilter: '/student/onboarding/provider-experience-filter',
  matchesLoading: '/student/onboarding/matches-loading',
  matches: '/student/onboarding/matches',
  match: '/student/onboarding/match',
  aboutYou: '/student/onboarding/about-you',
  insuranceInfo: '/student/onboarding/insurance-info',
  paymentInfo: '/student/onboarding/payment-info',
  confirmBooking: '/student/onboarding/confirm-booking',
  bookingSuccess: '/student/onboarding/booking-success',
  bookingFirstInterstitial: '/student/onboarding/booking-first-interstitial',
  bookingSecondInterstitial: '/student/onboarding/booking-second-interstitial',
  secondMatches: '/student/onboarding/second-matches',
  secondMatch: '/student/onboarding/second-match',
} as const;
export const STUDENT_ONBOARDING_ROUTES: string[] = Object.values(STUDENT_ONBOARDING_ROUTING_CONFIG);


export const BETTERHELP_ONBOARDING_ROUTING_CONFIG = {
  onboarding: '/betterhelp/onboarding',
  invalidLink: '/betterhelp/onboarding/invalid-link',
  welcome: '/betterhelp/onboarding/welcome',
  createAccount: '/betterhelp/onboarding/create-account',
  verifyPhone: '/betterhelp/onboarding/verify-phone',
  verifyPhoneCode: '/betterhelp/onboarding/verify-phone-code',
  where: '/betterhelp/onboarding/where',
  notAvailable: '/betterhelp/onboarding/not-available',
  insurance: '/betterhelp/onboarding/insurance',
  age: '/betterhelp/onboarding/age',
  tooYoung: '/betterhelp/onboarding/too-young',
  reason: '/betterhelp/onboarding/reason',
  providerFilters: '/betterhelp/onboarding/provider-filters',
  providerIdentityFilter: '/betterhelp/onboarding/provider-identity-filter',
  providerExperienceFilter: '/betterhelp/onboarding/provider-experience-filter',
  matchesLoading: '/betterhelp/onboarding/matches-loading',
  matches: '/betterhelp/onboarding/matches',
  match: '/betterhelp/onboarding/match',
  aboutYou: '/betterhelp/onboarding/about-you',
  insuranceInfo: '/betterhelp/onboarding/insurance-info',
  paymentInfo: '/betterhelp/onboarding/payment-info',
  confirmBooking: '/betterhelp/onboarding/confirm-booking',
  bookingSuccess: '/betterhelp/onboarding/booking-success',
  bookingInterstitial: '/betterhelp/onboarding/booking-interstitial',
} as const;
export const BETTERHELP_ONBOARDING_ROUTES: string[] = Object.values(BETTERHELP_ONBOARDING_ROUTING_CONFIG);


export const PROVIDER_ONBOARDING_ROUTES = [
  ROUTING_CONFIG.onboardingWelcome,
  ROUTING_CONFIG.onboardingLoginInfo,
  ROUTING_CONFIG.onboardingInvite,
  ROUTING_CONFIG.onboardingGeneral,
  ROUTING_CONFIG.onboardingPractice,
  ROUTING_CONFIG.onboardingClinical,
  ROUTING_CONFIG.onboardingFinance,
  ROUTING_CONFIG.onboardingComplete,
  ROUTING_CONFIG.onboardingPayorEnrollment,
  ROUTING_CONFIG.onboardingError
];

export const CLIENT_FORM_ROUTES = [
  ROUTING_CONFIG.formPage,
  ROUTING_CONFIG.formStart,
  ROUTING_CONFIG.formFinish,
];

const matchSessionRoutesRegex = /\/appointments\/.+\/session/g;

export const routeIsSession = (pathname = '') => {
  return (pathname.match(matchSessionRoutesRegex)?.length ?? 0) > 0;
};
